import { useLayoutEffect, useRef, useState, type PointerEvent } from 'react';
import { useCompanion } from '../state/companion.js';
import { useHealth } from '../state/health.js';
import { useMeetingRecorder } from '../state/meeting-recorder.js';
import { usePetVisible } from '../lib/pet.js';
import { Jabber, type JabberMood } from './Jabber.js';

const STORAGE_KEY = 'jabberr.mascot.position';
type Position = { x: number; y: number };
const home: Position = { x: 1, y: 0.85 };
const clamp = (n: number) => Math.max(0, Math.min(1, n));
function loadPosition(): Position {
  try {
    const p: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    if (p && typeof p === 'object' && 'x' in p && 'y' in p && typeof p.x === 'number' && typeof p.y === 'number' && Number.isFinite(p.x) && Number.isFinite(p.y)) {
      return { x: clamp(p.x), y: clamp(p.y) };
    }
  } catch { /* Storage is optional in private/restricted browsers. */ }
  return home;
}

/**
 * Jabberr as a pet: hidden unless the person turns it on (sidebar user card). It only shows a mood; messages
 * that need a look (meetings, replies needing attention) go to NoticeBanner, never to a flag over its head.
 */
export function JabberCompanion() {
  const visible = usePetVisible();
  return visible ? <Pet /> : null;
}

function Pet() {
  const { waiting: chatWaiting } = useCompanion();
  const meetings = useMeetingRecorder();
  const { aiState } = useHealth();
  const waiting = chatWaiting || meetings.pending > 0;
  const [playful, setPlayful] = useState(false);
  const [open, setOpen] = useState(false);
  const moved = useRef(false);
  const [position, setPosition] = useState(loadPosition);
  const [bounds, setBounds] = useState({ width: 0, height: 0 });
  const float = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: number; x: number; y: number; start: Position } | null>(null);
  const latest = useRef(position);
  const move = (next: Position, save = false) => {
    latest.current = next;
    setPosition(next);
    if (save) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* Still works without storage. */ }
    }
  };
  useLayoutEffect(() => {
    const el = float.current;
    if (!el) return;
    const resize = () => setBounds({ width: Math.max(0, window.innerWidth - el.offsetWidth - 40), height: Math.max(0, window.innerHeight - el.offsetHeight - 40) });
    window.addEventListener('resize', resize);
    resize();
    return () => window.removeEventListener('resize', resize);
  }, []);
  const finish = (e: PointerEvent<HTMLButtonElement>) => {
    if (drag.current?.id !== e.pointerId) return;
    drag.current = null;
    move(latest.current, true);
  };
  const mood: JabberMood = playful ? 'jump' : aiState === 'unavailable' ? 'blocked' : waiting ? 'working' : open ? 'wave' : 'idle';
  return (
    <div
      ref={float}
      className="jabber-float"
      style={{ left: 20 + position.x * bounds.width, top: 6 + position.y * bounds.height }}
      onPointerEnter={() => setOpen(true)}
      onPointerLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        className="jabber-drag-handle"
        aria-label="Jabberr, your pet. Drag or use arrow keys to move; Home resets."
        title="Drag to move Jabberr. Arrow keys move; Home resets."
        onClick={() => {
          if (moved.current) return;
          setPlayful(true);
          setTimeout(() => setPlayful(false), 1600);
        }}
        onPointerDown={(e) => {
          moved.current = false;
          if (e.button !== 0 || window.matchMedia('(max-width: 600px)').matches) return;
          drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, start: position };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d || d.id !== e.pointerId) return;
          if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > 5) moved.current = true;
          move({ x: clamp(d.start.x + (e.clientX - d.x) / (bounds.width || 1)), y: clamp(d.start.y + (e.clientY - d.y) / (bounds.height || 1)) });
        }}
        onPointerUp={finish}
        onPointerCancel={finish}
        onLostPointerCapture={finish}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') moved.current = false;
          if (e.key === 'Home') { e.preventDefault(); move(home, true); }
          if (window.matchMedia('(max-width: 600px)').matches) return;
          const steps: Record<string, Position> = { ArrowLeft: { x: -20, y: 0 }, ArrowRight: { x: 20, y: 0 }, ArrowUp: { x: 0, y: -20 }, ArrowDown: { x: 0, y: 20 } };
          const step = steps[e.key];
          if (step) { e.preventDefault(); move({ x: clamp(position.x + step.x / (bounds.width || 1)), y: clamp(position.y + step.y / (bounds.height || 1)) }, true); }
        }}
      ><Jabber mood={mood} size="companion" /></button>
    </div>
  );
}
